import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { advisorOnboardingKeys, subscriptionSeatsKeys } from '@client/api/waterlily';

/**
 * Detects when returning from Stripe checkout and:
 * 1. Invalidates the subscription seats query to fetch fresh subscription data
 * 2. Invalidates the onboarding status query so the server recomputes steps
 *
 * Note: The 'activate-seat' step is now computed server-side based on subscription status,
 * so we just need to invalidate the cache to trigger a refetch.
 */
export function useStripeCheckoutReturn() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { advisorId } = useParams<{ advisorId: string }>();

  const hasHandledSuccessRef = useRef(false);

  useEffect(() => {
    const isReturningFromCheckout =
      searchParams.get('session_id') ||
      searchParams.get('payment_status') === 'paid' ||
      location.pathname.includes('welcome');

    const isPaymentSuccessful = searchParams.get('payment_status') === 'paid';

    if (isReturningFromCheckout && advisorId) {
      // Invalidate React Query cache to fetch fresh subscription data
      queryClient.invalidateQueries({
        queryKey: subscriptionSeatsKeys.byAdvisor(advisorId),
      });

      // Invalidate onboarding status so server recomputes 'activate-seat' step
      if (isPaymentSuccessful && !hasHandledSuccessRef.current) {
        hasHandledSuccessRef.current = true;
        queryClient.invalidateQueries({
          queryKey: advisorOnboardingKeys.status(advisorId),
        });
      }
    }
  }, [location.pathname, searchParams, advisorId, queryClient]);
}
